import { useState } from 'react';
import { agencyRoleLabelEn } from '@/utils/agencyRole';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useAdminAgency,
  useDeleteAgency,
  useAdminPackages,
  useEntityAuditLogs,
  useUpdateAgencyInfo,
  useAddAgencyStaffAdmin,
  useRemoveAgencyStaffAdmin,
  useChangeAgencyStaffRoleAdmin,
  useChangeAgencyStaffReviewerAdmin,
  useAttachClientTeam,
  useDetachClientTeam,
  useAdminUsers,
  useAdminTeams,
} from '@/hooks/useAdmin';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { ConfirmDialog, Card, CardHeader, CardBody, Badge, EmptyState, Icon, Toggle } from '@/components/ui';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { formatApiError } from '@/services/api';
import { Combobox } from '@/components/ui/Combobox';
import { useDebounce } from '@/hooks/useDebounce';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import {
  grantAgencyAddon,
  revokeAgencyAddon,
  extendAgencyExpiry,
  changeAgencyPackage,
  changeAgencyStatus,
} from '@/services/admin.service';
import type {
  AuditLogItem,
  AgencyStaffRole,
  UpdateAgencyRequest,
  AddAgencyStaffAdminRequest,
} from '@/types/admin';
import { isLastReviewerCancel } from '@/stores/lastReviewerConfirm.store';

/**
 * Admin Agency Detail Page
 * View and manage individual agency details, clients, and staff
 */

const AGENCY_STAFF_ROLES: AgencyStaffRole[] = ['OWNER', 'ADMIN', 'MANAGER', 'MEMBER'];

export const AdminAgencyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDateTime, formatDateOnly } = useTimezone();
  const agencyId = parseInt(id || '0', 10);

  const { data: agency, isLoading, error } = useAdminAgency(agencyId);
  const deleteAgency = useDeleteAgency();

  const [deleteDialog, setDeleteDialog] = useState(false);

  // Extend expiry state
  const [isExtendingExpiry, setIsExtendingExpiry] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [expiryReason, setExpiryReason] = useState('');
  const [isExtending, setIsExtending] = useState(false);

  // Package change state
  const [isChangingPackage, setIsChangingPackage] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [packageReason, setPackageReason] = useState('');
  const [isChangingPkg, setIsChangingPkg] = useState(false);

  // Status change state
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [isChangingStat, setIsChangingStat] = useState(false);

  // Addon state
  const [isGrantingAddon, setIsGrantingAddon] = useState(false);
  const [selectedAddonType, setSelectedAddonType] = useState<'WHITELABEL' | 'EXTRA_STORAGE' | 'PRIORITY_SUPPORT' | null>(null);
  const [addonIsLifetime, setAddonIsLifetime] = useState(false);
  const [addonExpiresAt, setAddonExpiresAt] = useState('');
  const [addonStorageGb, setAddonStorageGb] = useState('');
  const [addonWcSubscriptionId, setAddonWcSubscriptionId] = useState('');
  const [isGrantingAddonLoading, setIsGrantingAddonLoading] = useState(false);

  const [revokeAddonDialog, setRevokeAddonDialog] = useState<{ open: boolean; addonId: number | null; addonType: string }>({ open: false, addonId: null, addonType: '' });
  const [isRevokingAddon, setIsRevokingAddon] = useState(false);

  // Edit Agency modal
  const [showEditModal, setShowEditModal] = useState(false);

  // Staff section modals
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [changeRoleTarget, setChangeRoleTarget] = useState<{ userId: number; fullName: string | null; email: string; role: string } | null>(null);
  const [removeStaffDialog, setRemoveStaffDialog] = useState<{ open: boolean; userId: number | null; name: string }>({ open: false, userId: null, name: '' });

  // Client teams section modals
  const [showAttachTeamModal, setShowAttachTeamModal] = useState(false);
  const [detachClientDialog, setDetachClientDialog] = useState<{ open: boolean; teamId: number | null; name: string }>({ open: false, teamId: null, name: '' });

  // Activity History state
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 20;

  const STATUS_OPTIONS = ['ACTIVE', 'GRACE_PERIOD', 'EXPIRED', 'CANCELLED', 'SUSPENDED'];

  const ADDON_TYPES: Array<'WHITELABEL' | 'EXTRA_STORAGE' | 'PRIORITY_SUPPORT'> = [
    'WHITELABEL',
    'EXTRA_STORAGE',
    'PRIORITY_SUPPORT',
  ];

  // Fetch packages for dropdown
  const { data: packagesData } = useAdminPackages({ isActive: true });
  const allPackages = (packagesData as { data?: { id: number; name: string; isAgency?: boolean }[] })?.data || [];
  const packages = allPackages.filter(pkg => pkg.isAgency);

  // Mutations
  const removeStaff = useRemoveAgencyStaffAdmin(agencyId);
  const changeReviewer = useChangeAgencyStaffReviewerAdmin(agencyId);
  const detachClient = useDetachClientTeam(agencyId);

  // Fetch audit logs
  const { data: auditLogsData, isLoading: isLoadingHistory, error: historyError } = useEntityAuditLogs(
    'AGENCY',
    agencyId,
    historyPage,
    historyLimit
  );

  const auditLogs = auditLogsData?.data || [];
  const auditLogsMeta = auditLogsData?.meta;

  // Helper function to format action types
  const formatActionType = (action: string): string => {
    const actionMap: Record<string, string> = {
      'AGENCY_CREATE': 'Brand Portfolio Created',
      'AGENCY_UPDATE': 'Brand Portfolio Updated',
      'AGENCY_PACKAGE_CHANGE': 'Package Changed',
      'AGENCY_EXPIRY_EXTEND': 'Expiry Extended',
      'AGENCY_EXTEND_EXPIRY': 'Expiry Extended',
      'AGENCY_STATUS_CHANGE': 'Status Changed',
      'AGENCY_ADDON_GRANT': 'Addon Granted',
      'AGENCY_ADDON_REVOKE': 'Addon Revoked',
      'AGENCY_DELETE': 'Brand Portfolio Deleted',
      'AGENCY_VIEW': 'Brand Portfolio Viewed',
      'TEAM_TO_AGENCY_CONVERT': 'Team Converted to Brand Portfolio',
    };
    return actionMap[action] || action;
  };

  // Helper function to format changes
  const formatChanges = (prev: Record<string, unknown> | null, next: Record<string, unknown> | null): string | null => {
    if (!prev || !next) return null;

    const changes: string[] = [];
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);

    keys.forEach(key => {
      if (prev[key] !== next[key]) {
        changes.push(`${key}: ${prev[key]} → ${next[key]}`);
      }
    });

    return changes.length > 0 ? changes.join(', ') : null;
  };

  // Helper function to get icon and color for action type
  const getActionIcon = (action: string): { icon: string; colorClass: string; bgClass: string } => {
    if (action.includes('CREATE') || action.includes('GRANT')) {
      return { icon: ICONS.PLUS_CIRCLE, colorClass: 'text-green-600', bgClass: 'bg-green-100' };
    }
    if (action.includes('DELETE') || action.includes('REVOKE')) {
      return { icon: ICONS.TRASH, colorClass: 'text-red-600', bgClass: 'bg-red-100' };
    }
    if (action.includes('STATUS')) {
      return { icon: ICONS.PULSE, colorClass: 'text-orange-600', bgClass: 'bg-orange-100' };
    }
    return { icon: ICONS.EDIT, colorClass: 'text-primary-700', bgClass: 'bg-primary-50' };
  };

  const handleDeleteConfirm = async () => {
    if (!agency) return;

    try {
      await deleteAgency.mutateAsync(agency.id);
      toast.success(`Brand Portfolio "${agency.name}" deleted`);
      setDeleteDialog(false);
      navigate('/admin/agencies');
    } catch (err) {
      toast.error('Failed to delete brand portfolio');
    }
  };

  const handleExtendExpiry = async () => {
    if (!agency) return;
    setIsExtending(true);
    try {
      await extendAgencyExpiry(agency.id, { days: expiryDays, reason: expiryReason || undefined });
      toast.success(`Expiry extended by ${expiryDays} days`);
      setIsExtendingExpiry(false);
      setExpiryDays(30);
      setExpiryReason('');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to extend expiry');
    } finally {
      setIsExtending(false);
    }
  };

  const handleChangePackage = async () => {
    if (!agency || !selectedPackageId) return;
    setIsChangingPkg(true);
    try {
      await changeAgencyPackage(agency.id, { packageId: selectedPackageId, reason: packageReason || undefined });
      toast.success('Package changed successfully');
      setIsChangingPackage(false);
      setSelectedPackageId(null);
      setPackageReason('');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to change package');
    } finally {
      setIsChangingPkg(false);
    }
  };

  const handleChangeStatus = async () => {
    if (!agency || !selectedStatus) return;
    setIsChangingStat(true);
    try {
      await changeAgencyStatus(agency.id, { status: selectedStatus, reason: statusReason || undefined });
      toast.success(`Status changed to ${selectedStatus}`);
      setIsChangingStatus(false);
      setSelectedStatus(null);
      setStatusReason('');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to change status');
    } finally {
      setIsChangingStat(false);
    }
  };

  const handleGrantAddon = async () => {
    if (!agency || !selectedAddonType) return;
    setIsGrantingAddonLoading(true);
    try {
      await grantAgencyAddon(agency.id, {
        addonType: selectedAddonType,
        isLifetime: addonIsLifetime,
        expiresAt: addonIsLifetime || !addonExpiresAt ? undefined : addonExpiresAt,
        // EXTRA_STORAGE on a portfolio = the shared storage pool of all its brands.
        ...(selectedAddonType === 'EXTRA_STORAGE' ? { storageMb: Math.round(parseFloat(addonStorageGb) * 1024) } : {}),
        ...(selectedAddonType === 'EXTRA_STORAGE' && addonWcSubscriptionId.trim() ? { wcSubscriptionId: addonWcSubscriptionId.trim() } : {}),
      });
      toast.success(`${selectedAddonType} addon granted`);
      setIsGrantingAddon(false);
      setSelectedAddonType(null);
      setAddonIsLifetime(false);
      setAddonExpiresAt('');
      setAddonStorageGb('');
      setAddonWcSubscriptionId('');
      window.location.reload();
    } catch (err) {
      // Server message (409 duplicate subscription, 400 validation, ...) instead of a generic failure.
      toast.error(formatApiError(err).message || 'Failed to grant addon');
    } finally {
      setIsGrantingAddonLoading(false);
    }
  };

  const handleRevokeAddon = async () => {
    if (!agency || !revokeAddonDialog.addonId) return;
    setIsRevokingAddon(true);
    try {
      await revokeAgencyAddon(agency.id, revokeAddonDialog.addonId);
      toast.success(`${revokeAddonDialog.addonType} addon revoked`);
      setRevokeAddonDialog({ open: false, addonId: null, addonType: '' });
      window.location.reload();
    } catch (err) {
      toast.error('Failed to revoke addon');
    } finally {
      setIsRevokingAddon(false);
    }
  };

  const handleReviewerToggle = async (userId: number, name: string, isReviewer: boolean) => {
    try {
      await changeReviewer.mutateAsync({ userId, body: { isReviewer } });
      toast.success(isReviewer ? `${name} is now a reviewer` : `${name} is no longer a reviewer`);
    } catch (err) {
      // 409 release.last_reviewer is confirmed/cancelled by the global dialog (services/api.ts)
      if (isLastReviewerCancel(err)) return;
      toast.error(formatApiError(err).message);
    }
  };

  const handleRemoveStaffConfirm = async () => {
    if (!removeStaffDialog.userId) return;
    try {
      await removeStaff.mutateAsync(removeStaffDialog.userId);
      toast.success(`${removeStaffDialog.name} removed from brand portfolio`);
      setRemoveStaffDialog({ open: false, userId: null, name: '' });
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error(formatApiError(err).message);
      setRemoveStaffDialog({ open: false, userId: null, name: '' });
    }
  };

  const handleDetachClientConfirm = async () => {
    if (!detachClientDialog.teamId) return;
    try {
      await detachClient.mutateAsync(detachClientDialog.teamId);
      toast.success(`${detachClientDialog.name} detached from brand portfolio`);
      setDetachClientDialog({ open: false, teamId: null, name: '' });
    } catch (err) {
      toast.error(formatApiError(err).message);
      setDetachClientDialog({ open: false, teamId: null, name: '' });
    }
  };

  // Status colors span the full 5-state agency/team lifecycle (incl.
  // SUSPENDED=orange), which falls outside Badge's semantic palette — kept
  // as a local color map rather than forcing into <Badge variant>, matching
  // the same status set on Teams.tsx / TeamDetail.tsx.
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-700';
      case 'GRACE_PERIOD':
        return 'bg-yellow-100 text-yellow-700';
      case 'EXPIRED':
        return 'bg-red-100 text-red-700';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-700';
      case 'SUSPENDED':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/admin/agencies')}>
            Back to Brand Portfolios
          </Button>
        </div>
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading agency: {formatApiError(error).message}
        </div>
      </div>
    );
  }

  if (isLoading || !agency) {
    return (
      <div className="p-8 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <AdminPageHeader
        title={agency.name}
        subtitle={agency.slug}
        backTo="/admin/agencies"
        backLabel="Back to Brand Portfolios"
        action={
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate(`/agency/settings/whitelabel`)}
            >
              <Icon name={ICONS.PAINT} size="sm" className="mr-2" />
              Atelier Mode Settings
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowEditModal(true)}
            >
              <Icon name={ICONS.EDIT} size="sm" className="mr-2" />
              Edit Brand Portfolio
            </Button>
            <Button
              variant="danger"
              onClick={() => setDeleteDialog(true)}
              disabled={deleteAgency.isPending}
            >
              Delete Brand Portfolio
            </Button>
          </div>
        }
      />

      {/* Agency Info Card */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Brand Portfolio Information</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Name</label>
              <p className="mt-1 text-gray-900">{agency.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Slug</label>
              <p className="mt-1 text-gray-900">{agency.slug}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Owner Name</label>
              <p className="mt-1 text-gray-900">{agency.ownerName || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Owner Email</label>
              <p className="mt-1 text-gray-900">{agency.ownerEmail || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Phone</label>
              <p className="mt-1 text-gray-900">{agency.phone || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Client Teams</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{agency.clientCount}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Staff Members</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{agency.staffCount}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Created</label>
              <p className="mt-1 text-gray-900">{formatDateTime(agency.createdAt)}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Package & Subscription */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Package & Subscription</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Current Package</label>
              <p className="mt-1">
                <Badge variant="info">{agency.packageName || 'No package'}</Badge>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(agency.status || 'ACTIVE')}`}>
                  {agency.status || 'ACTIVE'}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Expires At</label>
              <p className="mt-1 text-gray-900">
                {agency.expiresAt ? formatDateOnly(agency.expiresAt) : 'Never'}
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            {!isChangingPackage && !isExtendingExpiry && (
              <>
                <Button variant="secondary" onClick={() => setIsChangingPackage(true)}>
                  <Icon name={ICONS.EDIT} size="sm" className="mr-2" />
                  Change Package
                </Button>
                <Button variant="secondary" onClick={() => setIsExtendingExpiry(true)}>
                  <Icon name={ICONS.CLOCK} size="sm" className="mr-2" />
                  Extend Expiry
                </Button>
              </>
            )}
          </div>

          {isChangingPackage && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  New Package
                </label>
                <Select
                  value={selectedPackageId?.toString() || ''}
                  onValueChange={(value) => setSelectedPackageId(value ? parseInt(value, 10) : null)}
                  placeholder="Select a package..."
                  options={packages.map((pkg) => ({
                    value: pkg.id.toString(),
                    label: `${pkg.name}${pkg.id === agency.packageId ? ' (current)' : ''}`
                  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  Reason (optional)
                </label>
                <Input
                  type="text"
                  value={packageReason}
                  onChange={(e) => setPackageReason(e.target.value)}
                  placeholder="Reason for change"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleChangePackage} disabled={!selectedPackageId || isChangingPkg} loading={isChangingPkg}>
                  Save Package
                </Button>
                <Button variant="secondary" onClick={() => { setIsChangingPackage(false); setSelectedPackageId(null); setPackageReason(''); }} disabled={isChangingPkg}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {isExtendingExpiry && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  Days to Extend
                </label>
                <Input
                  type="number"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(parseInt(e.target.value, 10) || 0)}
                  min="1"
                  max="365"
                  className="w-24"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  Reason (optional)
                </label>
                <Input
                  type="text"
                  value={expiryReason}
                  onChange={(e) => setExpiryReason(e.target.value)}
                  placeholder="Reason for extension"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleExtendExpiry}
                  disabled={expiryDays < 1 || isExtending}
                  loading={isExtending}
                >
                  Extend Expiry
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsExtendingExpiry(false);
                    setExpiryDays(30);
                    setExpiryReason('');
                  }}
                  disabled={isExtending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Status Management */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Status Management</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          {!isChangingStatus ? (
            <Button variant="secondary" onClick={() => setIsChangingStatus(true)}>
              <Icon name={ICONS.TOOLS} size="sm" className="mr-2" />
              Change Status
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">New Status</label>
                <Select
                  value={selectedStatus || agency.status || 'ACTIVE'}
                  onValueChange={(value) => setSelectedStatus(value)}
                  options={STATUS_OPTIONS.map(status => ({
                    value: status,
                    label: status
                  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Reason (optional)</label>
                <Input
                  type="text"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Reason for status change"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleChangeStatus}
                  disabled={!selectedStatus || selectedStatus === agency.status || isChangingStat}
                  loading={isChangingStat}
                >
                  Save Status
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => { setIsChangingStatus(false); setSelectedStatus(null); setStatusReason(''); }}
                  disabled={isChangingStat}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Shared storage pool of the portfolio */}
      {agency.storagePool && (
        <Card padding="none">
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900">Storage pool</h2>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-gray-600">
              {agency.storagePool.poolUsedMb.toLocaleString()} MB used of {agency.storagePool.poolMb.toLocaleString()} MB. Each brand keeps its plan storage; only the overflow above it is drawn from the pool.
            </p>
            {agency.storagePool.brands.length === 0 ? (
              <p className="text-sm text-gray-500">No brand is using the pool.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {agency.storagePool.brands.map((brand) => (
                  <li key={brand.teamId} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-gray-900">
                      {brand.name}
                      {!brand.isActive && <span className="ml-2 text-gray-500">(paused or hidden)</span>}
                    </span>
                    <span className="text-gray-600">
                      {brand.overflowMb.toLocaleString()} MB over its {brand.includedMb !== null ? `${brand.includedMb.toLocaleString()} MB` : 'plan'} ({brand.usedMb.toLocaleString()} MB used)
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      {/* Addons */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Addons</h2>
          {!isGrantingAddon && (
            <Button variant="secondary" size="sm" onClick={() => setIsGrantingAddon(true)}>
              <Icon name={ICONS.PLUS} size="sm" className="mr-2" />
              Grant Addon
            </Button>
          )}
        </CardHeader>
        <CardBody className="space-y-4">
          {(!agency.addons || agency.addons.length === 0) ? (
            <EmptyState title="No addons granted" />
          ) : (
            <div className="space-y-2">
              {agency.addons.map((addon) => (
                <div
                  key={addon.id}
                  className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {addon.addonType}
                      {addon.addonType === 'EXTRA_STORAGE' && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          {addon.storageMb ? `Pool +${(addon.storageMb / 1024).toLocaleString()} GB` : 'no amount (inactive)'}
                          {addon.purchaseId ? ` · WC subscription #${addon.purchaseId}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {addon.isLifetime ? (
                        <span className="text-green-600 font-medium">Lifetime</span>
                      ) : (
                        <>Expires: {addon.expiresAt ? formatDateOnly(addon.expiresAt) : 'Never'}</>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={addon.isActive ? 'success' : 'danger'}>
                      {addon.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setRevokeAddonDialog({ open: true, addonId: addon.id, addonType: addon.addonType })}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isGrantingAddon && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  Addon Type
                </label>
                <Select
                  value={selectedAddonType || ''}
                  onValueChange={(value) => setSelectedAddonType(value as typeof ADDON_TYPES[number])}
                  placeholder="Select addon type"
                  options={ADDON_TYPES.map(type => ({
                    value: type,
                    label: type
                  }))}
                />
              </div>
              {selectedAddonType === 'EXTRA_STORAGE' && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">Extra storage for the shared pool (GB)</label>
                  <input type="number" value={addonStorageGb} onChange={(e) => setAddonStorageGb(e.target.value)} min="1" step="1" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400" />
                  <label className="text-sm font-medium text-gray-600 block mt-3 mb-2">WooCommerce subscription id (optional, needs an end date)</label>
                  <input type="text" inputMode="numeric" value={addonWcSubscriptionId} onChange={(e) => setAddonWcSubscriptionId(e.target.value.replace(/\D/g, '').replace(/^0+/, ''))} placeholder="e.g. 14200" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="agencyAddonLifetime"
                  checked={addonIsLifetime}
                  onChange={(e) => setAddonIsLifetime(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
                />
                <label htmlFor="agencyAddonLifetime" className="text-sm font-medium text-gray-600">
                  Lifetime addon
                </label>
              </div>
              {!addonIsLifetime && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">
                    Expires At {selectedAddonType === 'EXTRA_STORAGE' ? '(required)' : '(optional)'}
                  </label>
                  <DatePickerSingle
                    value={addonExpiresAt}
                    onChange={(iso) => setAddonExpiresAt(iso || '')}
                    placeholder="dd.mm.yyyy"
                    minDate={new Date()}
                  />
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleGrantAddon}
                  disabled={!selectedAddonType || (selectedAddonType === 'EXTRA_STORAGE' && (!(parseFloat(addonStorageGb) >= 1) || (!addonIsLifetime && !addonExpiresAt) || (addonWcSubscriptionId !== '' && addonIsLifetime))) || isGrantingAddonLoading}
                  loading={isGrantingAddonLoading}
                >
                  Grant Addon
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsGrantingAddon(false);
                    setSelectedAddonType(null);
                    setAddonIsLifetime(false);
                    setAddonExpiresAt('');
                    setAddonStorageGb('');
                    setAddonWcSubscriptionId('');
                  }}
                  disabled={isGrantingAddonLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Client Teams */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Client Teams ({(agency.clients || []).length})</h2>
          <Button variant="secondary" size="sm" onClick={() => setShowAttachTeamModal(true)}>
            <Icon name={ICONS.PLUS} size="sm" className="mr-2" />
            Attach Team
          </Button>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(agency.clients || []).length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <EmptyState title="No client teams found" />
                  </td>
                </tr>
              ) : (
                (agency.clients || []).map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {client.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(client.status)}`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {client.expiresAt ? formatDateOnly(client.expiresAt) : 'Never'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/teams/${client.id}`}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          View Details
                        </Link>
                        <button
                          type="button"
                          title="Detach from brand portfolio"
                          aria-label={`Detach ${client.name} from brand portfolio`}
                          onClick={() => setDetachClientDialog({ open: true, teamId: client.id, name: client.name })}
                          className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Icon name={ICONS.TRASH} size="xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Staff Members */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Staff Members ({(agency.staff || []).length})</h2>
          <Button variant="secondary" size="sm" onClick={() => setShowAddStaffModal(true)}>
            <Icon name={ICONS.PLUS} size="sm" className="mr-2" />
            Add Staff
          </Button>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  All Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reviewer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(agency.staff || []).length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No staff members found" />
                  </td>
                </tr>
              ) : (
                (agency.staff || []).map((staff) => (
                  <tr key={staff.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {staff.fullName || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{staff.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="info">{agencyRoleLabelEn(staff.role)}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={staff.role === 'MANAGER' && staff.canEnterAllClients ? 'success' : 'neutral'}>
                        {staff.role === 'MANAGER' && staff.canEnterAllClients ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Toggle
                        label={`Reviewer: ${staff.fullName || staff.email}`}
                        checked={staff.isReviewer}
                        disabled={changeReviewer.isPending}
                        onChange={(next) => handleReviewerToggle(staff.userId, staff.fullName || staff.email, next)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/users/${staff.userId}`}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          View Details
                        </Link>
                        {/* Change Role — visually de-emphasised for OWNER to avoid accidental last-owner demotion */}
                        <button
                          type="button"
                          title="Change Role"
                          aria-label={`Change role of ${staff.fullName || staff.email}`}
                          onClick={() => setChangeRoleTarget({ userId: staff.userId, fullName: staff.fullName, email: staff.email, role: staff.role })}
                          className={`p-1 rounded ${
                            staff.role === 'OWNER'
                              ? 'text-gray-300 cursor-default'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                          disabled={staff.role === 'OWNER'}
                        >
                          <Icon name={ICONS.EDIT} size="xs" />
                        </button>
                        {/* Remove */}
                        <button
                          type="button"
                          title="Remove Staff"
                          aria-label={`Remove ${staff.fullName || staff.email} from brand portfolio`}
                          onClick={() => setRemoveStaffDialog({ open: true, userId: staff.userId, name: staff.fullName || staff.email })}
                          className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Icon name={ICONS.TRASH} size="xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Activity History */}
      <Card padding="none">
        <button
          onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
          className="w-full px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Activity History</h2>
            {auditLogsMeta && (
              <Badge variant="neutral">{auditLogsMeta.total}</Badge>
            )}
          </div>
          <Icon name={isHistoryExpanded ? ICONS.ANGLE_UP : ICONS.ANGLE_DOWN} size="sm" className="text-gray-600" />
        </button>

        {isHistoryExpanded && (
          <CardBody>
            {isLoadingHistory ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : historyError ? (
              <div className="text-center py-8 text-red-600">
                Failed to load activity history
              </div>
            ) : auditLogs.length === 0 ? (
              <EmptyState title="No activity history available" />
            ) : (
              <div className="space-y-6">
                {/* Timeline */}
                <div className="space-y-0">
                  {auditLogs.map((log: AuditLogItem, index: number) => {
                    const { icon, colorClass, bgClass } = getActionIcon(log.action);
                    const changesText = formatChanges(log.previousValues, log.newValues);
                    const reason = log.metadata?.reason ? String(log.metadata.reason) : null;

                    return (
                      <div
                        key={log.id}
                        className={`relative pl-8 pb-6 ${
                          index !== auditLogs.length - 1 ? 'border-l-2 border-gray-200' : ''
                        }`}
                      >
                        <div className={`absolute -left-4 top-0 w-8 h-8 rounded-full ${bgClass} flex items-center justify-center`}>
                          <Icon name={icon} size="sm" className={colorClass} />
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{formatActionType(log.action)}</span>
                            <span className="text-sm text-gray-500">{formatDateTime(log.createdAt)}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            by {log.adminName || log.adminEmail}
                          </div>
                          {reason && (
                            <div className="text-sm text-gray-500 italic">
                              Reason: {reason}
                            </div>
                          )}
                          {changesText && (
                            <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded mt-2">
                              {changesText}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {auditLogsMeta && auditLogsMeta.totalPages > 1 && (
                  <div className="border-t pt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Showing {auditLogs.length} of {auditLogsMeta.total}
                    </div>
                    {historyPage < auditLogsMeta.totalPages && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setHistoryPage(historyPage + 1)}
                      >
                        Load More
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardBody>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        title="Delete Brand Portfolio"
        description={`Are you sure you want to delete "${agency.name}"?\n\nThis will affect ${agency.clientCount} client teams.\n\nThis action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteAgency.isPending}
      />

      {/* Revoke Addon Confirmation Dialog */}
      <ConfirmDialog
        open={revokeAddonDialog.open}
        onOpenChange={(open) => !open && setRevokeAddonDialog({ open: false, addonId: null, addonType: '' })}
        title="Revoke Addon"
        description={`Are you sure you want to revoke the ${revokeAddonDialog.addonType} addon? This will remove the addon from this brand portfolio.`}
        confirmText="Revoke"
        variant="danger"
        onConfirm={handleRevokeAddon}
        loading={isRevokingAddon}
      />

      {/* Remove Staff Confirmation Dialog */}
      <ConfirmDialog
        open={removeStaffDialog.open}
        onOpenChange={(open) => !open && setRemoveStaffDialog({ open: false, userId: null, name: '' })}
        title="Remove Staff Member"
        description={`Remove ${removeStaffDialog.name} from this brand portfolio? They will lose all brand portfolio access.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemoveStaffConfirm}
        loading={removeStaff.isPending}
      />

      {/* Detach Client Team Confirmation Dialog */}
      <ConfirmDialog
        open={detachClientDialog.open}
        onOpenChange={(open) => !open && setDetachClientDialog({ open: false, teamId: null, name: '' })}
        title="Detach Client Team"
        description={`Detach ${detachClientDialog.name} from this brand portfolio? The team will become standalone.`}
        confirmText="Detach"
        variant="danger"
        onConfirm={handleDetachClientConfirm}
        loading={detachClient.isPending}
      />

      {/* Local Modals */}
      {showEditModal && (
        <EditAgencyModal
          agencyId={agencyId}
          currentName={agency.name}
          currentOwnerEmail={agency.ownerEmail || ''}
          currentPhone={agency.phone || ''}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showAddStaffModal && (
        <AddStaffModal
          agencyId={agencyId}
          existingStaffUserIds={(agency.staff || []).map(s => s.userId)}
          onClose={() => setShowAddStaffModal(false)}
        />
      )}

      {changeRoleTarget && (
        <ChangeStaffRoleModal
          agencyId={agencyId}
          target={changeRoleTarget}
          onClose={() => setChangeRoleTarget(null)}
        />
      )}

      {showAttachTeamModal && (
        <AttachTeamModal
          agencyId={agencyId}
          onClose={() => setShowAttachTeamModal(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// LOCAL MODAL: Edit Agency
// ============================================================================

interface EditAgencyModalProps {
  agencyId: number;
  currentName: string;
  currentOwnerEmail: string;
  currentPhone: string;
  onClose: () => void;
}

const EditAgencyModal = ({ agencyId, currentName, currentOwnerEmail, currentPhone, onClose }: EditAgencyModalProps) => {
  const updateAgencyInfo = useUpdateAgencyInfo(agencyId);
  const [name, setName] = useState(currentName);
  const [ownerEmail, setOwnerEmail] = useState(currentOwnerEmail);
  const [phone, setPhone] = useState<string | null>(currentPhone || null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const trimmedOwnerEmail = ownerEmail.trim();
      const payload: UpdateAgencyRequest = {
        name: name.trim() || undefined,
        phone: phone?.trim() || null,
      };
      // Only send ownerEmail if it's non-empty and actually changed — never
      // send the dead `email` (agency contact) field from this modal.
      if (trimmedOwnerEmail && trimmedOwnerEmail !== currentOwnerEmail) {
        payload.ownerEmail = trimmedOwnerEmail;
      }
      await updateAgencyInfo.mutateAsync(payload);
      toast.success('Brand Portfolio updated');
      onClose();
    } catch (err) {
      toast.error(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Edit Brand Portfolio"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={updateAgencyInfo.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="edit-agency-form" disabled={updateAgencyInfo.isPending} loading={updateAgencyInfo.isPending}>
            Save Changes
          </Button>
        </ModalFooter>
      }
    >
      <form id="edit-agency-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Owner Email</label>
          <Input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Changes the owner's login email. Must not already be in use by another user.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <Input
            type="text"
            value={phone || ''}
            onChange={(e) => setPhone(e.target.value || null)}
            placeholder="+49 30 …"
          />
        </div>
      </form>
    </Modal>
  );
};

// ============================================================================
// LOCAL MODAL: Add Staff
// ============================================================================

interface AddStaffModalProps {
  agencyId: number;
  existingStaffUserIds: number[];
  onClose: () => void;
}

const AddStaffModal = ({ agencyId, existingStaffUserIds, onClose }: AddStaffModalProps) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<AgencyStaffRole>('MEMBER');
  const [title, setTitle] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  const searchQuery = useDebounce(inputValue, 300);

  const { data: usersData, isLoading: isLoadingUsers } = useAdminUsers({
    search: searchQuery || undefined,
    limit: 50,
    isActive: true,
  });

  const addStaff = useAddAgencyStaffAdmin(agencyId);

  const availableUsers = (usersData?.data ?? []).filter(u => !existingStaffUserIds.includes(u.id));

  const comboboxOptions = availableUsers.map(u => ({
    value: String(u.id),
    label: u.fullName ? `${u.fullName} (${u.email})` : u.email,
  }));

  const handleComboboxChange = (val: string) => {
    setInlineError(null);
    if (!isNaN(parseInt(val, 10))) {
      setSelectedUserId(val);
      const picked = comboboxOptions.find(o => o.value === val);
      setInputValue(picked?.label ?? val);
    } else {
      setSelectedUserId('');
      setInputValue(val);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (!selectedUserId) return;

    const parsedId = parseInt(selectedUserId, 10);
    if (isNaN(parsedId)) {
      setInlineError('Please select a user from the list.');
      return;
    }

    const body: AddAgencyStaffAdminRequest = {
      userId: parsedId,
      role: selectedRole,
      title: title.trim() || undefined,
    };

    try {
      await addStaff.mutateAsync(body);
      toast.success('Staff member added');
      onClose();
    } catch (err) {
      const apiErr = formatApiError(err);
      if (apiErr.status === 409) {
        setInlineError('This user is already a staff member of this brand portfolio.');
      } else {
        toast.error(apiErr.message);
      }
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Add Staff Member"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={addStaff.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="add-staff-form" disabled={!selectedUserId || addStaff.isPending} loading={addStaff.isPending}>
            Add Staff
          </Button>
        </ModalFooter>
      }
    >
      <form id="add-staff-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
          <Combobox
            value={inputValue}
            onChange={handleComboboxChange}
            options={comboboxOptions}
            placeholder="Search by name or email…"
            loading={isLoadingUsers}
            emptyMessage="No matching users"
          />
          <p className="text-xs text-gray-500 mt-1">Only users not already on this brand portfolio are shown.</p>
        </div>

        <Select
          label="Role"
          value={selectedRole}
          onValueChange={(v) => setSelectedRole(v as AgencyStaffRole)}
          options={AGENCY_STAFF_ROLES.map(r => ({ value: r, label: r }))}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Account Manager"
          />
        </div>

        {inlineError && <p className="text-sm text-red-600">{inlineError}</p>}
      </form>
    </Modal>
  );
};

// ============================================================================
// LOCAL MODAL: Change Staff Role
// ============================================================================

interface ChangeStaffRoleModalProps {
  agencyId: number;
  target: { userId: number; fullName: string | null; email: string; role: string };
  onClose: () => void;
}

const ChangeStaffRoleModal = ({ agencyId, target, onClose }: ChangeStaffRoleModalProps) => {
  const [selectedRole, setSelectedRole] = useState<AgencyStaffRole>(target.role as AgencyStaffRole);
  const changeRole = useChangeAgencyStaffRoleAdmin(agencyId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await changeRole.mutateAsync({ userId: target.userId, body: { role: selectedRole } });
      toast.success(`Role updated to ${selectedRole}`);
      onClose();
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Change Staff Role"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={changeRole.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="change-staff-role-form"
            disabled={selectedRole === (target.role as AgencyStaffRole) || changeRole.isPending}
            loading={changeRole.isPending}
          >
            Save Role
          </Button>
        </ModalFooter>
      }
    >
      <p className="text-sm text-gray-500 -mt-2 mb-4">{target.fullName || target.email}</p>
      <form id="change-staff-role-form" onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Role"
          value={selectedRole}
          onValueChange={(v) => setSelectedRole(v as AgencyStaffRole)}
          options={AGENCY_STAFF_ROLES.filter(r => r !== 'OWNER').map(r => ({ value: r, label: r }))}
        />
        <p className="text-xs text-gray-500">
          Demoting the only OWNER is blocked by the server.
        </p>
      </form>
    </Modal>
  );
};

// ============================================================================
// LOCAL MODAL: Attach Team
// ============================================================================

interface AttachTeamModalProps {
  agencyId: number;
  onClose: () => void;
}

const AttachTeamModal = ({ agencyId, onClose }: AttachTeamModalProps) => {
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  const searchQuery = useDebounce(inputValue, 300);

  // Fetch standalone teams (agencyId not set) filtered by search
  const { data: teamsData, isLoading: isLoadingTeams } = useAdminTeams({
    search: searchQuery || undefined,
    limit: 50,
  });

  // Only show teams with no agencyId (standalone)
  const availableTeams = (teamsData?.data ?? []).filter(t => t.agencyId === null);

  const comboboxOptions = availableTeams.map(t => ({
    value: String(t.id),
    label: t.name,
  }));

  const attachTeam = useAttachClientTeam(agencyId);

  const handleComboboxChange = (val: string) => {
    setInlineError(null);
    if (!isNaN(parseInt(val, 10))) {
      setSelectedTeamId(val);
      const picked = comboboxOptions.find(o => o.value === val);
      setInputValue(picked?.label ?? val);
    } else {
      setSelectedTeamId('');
      setInputValue(val);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (!selectedTeamId) return;

    const parsedId = parseInt(selectedTeamId, 10);
    if (isNaN(parsedId)) {
      setInlineError('Please select a team from the list.');
      return;
    }

    try {
      await attachTeam.mutateAsync({ teamId: parsedId });
      toast.success('Team attached to brand portfolio');
      onClose();
    } catch (err) {
      const apiErr = formatApiError(err);
      if (apiErr.status === 409) {
        setInlineError('This team is already attached to a brand portfolio.');
      } else {
        toast.error(apiErr.message);
      }
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Attach Client Team"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={attachTeam.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="attach-team-form" disabled={!selectedTeamId || attachTeam.isPending} loading={attachTeam.isPending}>
            Attach Team
          </Button>
        </ModalFooter>
      }
    >
      <form id="attach-team-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
          <Combobox
            value={inputValue}
            onChange={handleComboboxChange}
            options={comboboxOptions}
            placeholder="Search standalone teams…"
            loading={isLoadingTeams}
            emptyMessage="No standalone teams found"
          />
          <p className="text-xs text-gray-500 mt-1">Only teams not already attached to any brand portfolio are shown.</p>
        </div>

        {inlineError && <p className="text-sm text-red-600">{inlineError}</p>}
      </form>
    </Modal>
  );
};

export default AdminAgencyDetail;
